import type { DefaultError, QueryClient, QueryKey } from '@tanstack/query-core';
import type { QueryFunctionContext, UseSuspenseQueryOptions } from '@tanstack/react-query';
import { useSuspenseQuery } from '@tanstack/react-query';
import { usePageContext } from 'vike-react/usePageContext';

type Headers = Record<string, string> | undefined;

type QueryFnWithHeaders<TQueryFnData, TQueryKey extends readonly unknown[]> = (
    context: QueryFunctionContext<TQueryKey>,
    headers: Headers
) => Promise<TQueryFnData>;

type UseHeadersSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey extends readonly unknown[]>
    = Omit<UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn'> & {
        queryFn: QueryFnWithHeaders<TQueryFnData, TQueryKey>;
    };

export const useHeadersSuspenseQuery = <
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey
>(
    options: UseHeadersSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient?: QueryClient
) => {
    const pageContext = usePageContext();

    return useSuspenseQuery({
        ...options,
        queryFn: context =>
            options.queryFn(
                context,
                !pageContext.isClientSide ? pageContext.headers || undefined : undefined
            )
    }, queryClient);
};
